import type { Area } from "react-easy-crop";

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (err) => reject(err));
    image.src = url;
  });
}

const JPEG_TYPES = new Set(["image/jpeg", "image/jpg"]);
const WEBP = "image/webp";
const PNG = "image/png";

/** Output type for canvas: prefer to match source when the browser can encode it. */
export function getOutputMimeType(sourceType: string): "image/jpeg" | "image/png" | "image/webp" {
  const t = (sourceType || "").toLowerCase();
  if (t === WEBP) return "image/webp";
  if (t === PNG) return "image/png";
  if (JPEG_TYPES.has(t)) return "image/jpeg";
  return "image/jpeg";
}

function extensionForMime(m: string): "jpg" | "png" | "webp" {
  if (m === "image/webp") return "webp";
  if (m === "image/png") return "png";
  return "jpg";
}

function baseNameFromFileName(name: string): string {
  const i = name.lastIndexOf(".");
  return i > 0 ? name.slice(0, i) : name;
}

export function buildCroppedFileName(originalName: string, mime: string): string {
  const base = baseNameFromFileName(originalName) || "booze-photo";
  return `${base}-cropped.${extensionForMime(mime)}`;
}

export async function getCroppedImageBlob(
  imageSrc: string,
  pixelCrop: Area,
  outputMime: "image/jpeg" | "image/png" | "image/webp",
  quality = 0.92
): Promise<Blob> {
  if (pixelCrop.width < 1 || pixelCrop.height < 1) {
    throw new Error("Invalid crop area");
  }
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not get canvas context");
  }
  canvas.width = Math.round(pixelCrop.width);
  canvas.height = Math.round(pixelCrop.height);
  ctx.drawImage(
    image,
    Math.round(pixelCrop.x),
    Math.round(pixelCrop.y),
    Math.round(pixelCrop.width),
    Math.round(pixelCrop.height),
    0,
    0,
    canvas.width,
    canvas.height
  );
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Empty crop"))),
      outputMime,
      outputMime === "image/jpeg" ? quality : undefined
    );
  });
}

export async function getCroppedImageFile(
  imageSrc: string,
  pixelCrop: Area,
  sourceType: string,
  sourceFileName: string
): Promise<File> {
  const mime = getOutputMimeType(sourceType);
  const blob = await getCroppedImageBlob(imageSrc, pixelCrop, mime);
  return new File([blob], buildCroppedFileName(sourceFileName, mime), { type: mime });
}
